import React, { useState, Children } from 'react';
import { ArrowLeftIcon, XIcon, AlertCircleIcon, TrashIcon } from 'lucide-react';
interface EditRoomFormProps {
  roomData: any;
  onSave: (roomData: any) => void;
  onCancel: () => void;
  onNavigateToAssets: () => void;
}
export function EditRoomForm({
  roomData: initialRoomData,
  onSave,
  onCancel,
  onNavigateToAssets
}: EditRoomFormProps) {
  const [roomData, setRoomData] = useState(initialRoomData);
  const [activeTab, setActiveTab] = useState<'info' | 'media'>('info');
  const roomCategories = ['SUPERIOR', 'DELUXE', 'OVERWATER_VILLA', 'BEACH_VILLA', 'SUITE'];
  const roomViews = ['SEA_VIEW', 'GARDEN_VIEW', 'OCEAN_VIEW', 'LAGOON_VIEW', 'BEACH_VIEW'];
  const bedTypes = ['King', 'Queen', 'Double', 'Twin', 'Single', 'Sofa Bed', 'Bunk Bed'];
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      checked
    } = e.target;
    setRoomData(prev => ({
      ...prev,
      [name]: checked
    }));
  };
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === '' ? 0 : parseInt(value, 10);
    setRoomData(prev => ({
      ...prev,
      [name]: numValue
    }));
  };
  const handleBedConfigChange = (index: number, field: string, value: string) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    if (field === 'count') {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: parseInt(value, 10) || 1
      };
    } else {
      updatedConfigs[index] = {
        ...updatedConfigs[index],
        [field]: value
      };
    }
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const removeBedConfig = (index: number) => {
    const updatedConfigs = [...roomData.bedConfigurations];
    updatedConfigs.splice(index, 1);
    setRoomData(prev => ({
      ...prev,
      bedConfigurations: updatedConfigs
    }));
  };
  const handleRemoveAmenity = (amenity: string) => {
    setRoomData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((a: string) => a !== amenity)
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(roomData);
  };
  return <div className="space-y-6" data-id="element-1936">
      <div className="flex items-center" data-id="element-1937">
        <button onClick={onCancel} className="flex items-center text-gray-600 hover:text-gray-900" data-id="element-1938">
          <ArrowLeftIcon size={18} className="mr-1" data-id="element-1939" />
          <span data-id="element-1940">Edit Room</span>
        </button>
      </div>
      <div className="bg-white rounded-lg border border-gray-200" data-id="element-1941">
        {/* Tabs */}
        <div className="border-b border-gray-200" data-id="element-1942">
          <nav className="flex" data-id="element-1943">
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'info' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={() => setActiveTab('info')} data-id="element-1944">
              Room information
            </button>
            <button className={`px-6 py-4 text-sm font-medium ${activeTab === 'media' ? 'border-b-2 border-red-500 text-red-600' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} onClick={onNavigateToAssets} data-id="element-1945">
              Media assets
            </button>
          </nav>
        </div>
        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6" data-id="element-1946">
          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1947">
            <div data-id="element-1948">
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1949">
                Room name<span className="text-red-500" data-id="element-1950">*</span>
              </label>
              <input type="text" id="name" name="name" value={roomData.name} onChange={handleInputChange} required className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1951" />
            </div>
            <div data-id="element-1952">
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1953">
                Room category<span className="text-red-500" data-id="element-1954">*</span>
              </label>
              <div className="relative" data-id="element-1955">
                <div className="flex items-center" data-id="element-1956">
                  <input type="text" value={roomData.category} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-1957" />
                  <button type="button" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  category: ''
                }))} data-id="element-1958">
                    <XIcon size={16} data-id="element-1959" />
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-6" data-id="element-1960">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1961">
              Room code
            </label>
            <input type="text" id="code" name="code" value={roomData.code} onChange={handleInputChange} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1962" />
          </div>
          <div className="mb-6" data-id="element-1963">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1964">
              Description
            </label>
            <textarea id="description" name="description" value={roomData.description} onChange={handleInputChange} rows={4} className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1965" />
          </div>
          <div className="flex flex-wrap gap-6 mb-6" data-id="element-1966">
            <div className="flex items-center" data-id="element-1967">
              <input type="checkbox" id="isSmoking" name="isSmoking" checked={roomData.isSmoking} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1968" />
              <label htmlFor="isSmoking" className="ml-2 text-sm text-gray-700" data-id="element-1969">
                Smoking
              </label>
            </div>
            <div className="flex items-center" data-id="element-1970">
              <input type="checkbox" id="isAccessible" name="isAccessible" checked={roomData.isAccessible} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1971" />
              <label htmlFor="isAccessible" className="ml-2 text-sm text-gray-700" data-id="element-1972">
                Accessibility
              </label>
            </div>
            <div className="flex items-center" data-id="element-1973">
              <input type="checkbox" id="isActive" name="isActive" checked={roomData.isActive} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-1974" />
              <label htmlFor="isActive" className="ml-2 text-sm text-gray-700" data-id="element-1975">
                Status
              </label>
            </div>
          </div>
          {/* Room View and Size */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-1976">
            <div data-id="element-1977">
              <label htmlFor="view" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1978">
                Room view<span className="text-red-500" data-id="element-1979">*</span>
              </label>
              <div className="relative" data-id="element-1980">
                <div className="flex items-center" data-id="element-1981">
                  <input type="text" value={roomData.view} readOnly className="w-full p-2 border border-gray-300 rounded-md bg-gray-50" data-id="element-1982" />
                  <button type="button" className="absolute right-2 text-gray-400 hover:text-gray-600" onClick={() => setRoomData(prev => ({
                  ...prev,
                  view: ''
                }))} data-id="element-1983">
                    <XIcon size={16} data-id="element-1984" />
                  </button>
                </div>
              </div>
            </div>
            <div data-id="element-1985">
              <label htmlFor="size" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1986">
                Room size (sq m)
              </label>
              <input type="number" id="size" name="size" value={roomData.size} onChange={handleInputChange} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-1987" />
            </div>
          </div>
          {/* Room Amenities */}
          <div className="mb-6" data-id="element-1988">
            <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1989">
              Room amenities
            </label>
            <div className="flex flex-wrap gap-2" data-id="element-1990">
              {roomData.amenities.map((amenity: string) => <div key={amenity} className="flex items-center bg-gray-100 rounded-md px-2 py-1" data-id="element-1991">
                  <span className="text-sm" data-id="element-1992">{amenity}</span>
                  <button type="button" onClick={() => handleRemoveAmenity(amenity)} className="ml-1 text-gray-400 hover:text-gray-600" data-id="element-1993">
                    <XIcon size={12} data-id="element-1994" />
                  </button>
                </div>)}
            </div>
          </div>
          {/* Occupancy Settings */}
          <div className="mb-6" data-id="element-1995">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-1996">
              Occupancy Settings
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4" data-id="element-1997">
              <div data-id="element-1998">
                <label htmlFor="baseAdultsOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1999">
                  Base Adults Occupancy<span className="text-red-500" data-id="element-2000">*</span>
                </label>
                <input type="number" id="baseAdultsOccupancy" value={roomData.baseAdultsOccupancy} onChange={e => handleNumberChange('baseAdultsOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2001" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-2002">
                  Standard number of allowed adults for this room
                </p>
              </div>
              <div data-id="element-2003">
                <label htmlFor="baseChildrenOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2004">
                  Base Children Occupancy<span className="text-red-500" data-id="element-2005">*</span>
                </label>
                <input type="number" id="baseChildrenOccupancy" value={roomData.baseChildrenOccupancy} onChange={e => handleNumberChange('baseChildrenOccupancy', e.target.value)} min="0" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2006" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-2007">
                  Standard number of allowed children for this room
                </p>
              </div>
              <div data-id="element-2008">
                <label htmlFor="maxOccupancy" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2009">
                  Maximum Occupancy<span className="text-red-500" data-id="element-2010">*</span>
                </label>
                <input type="number" id="maxOccupancy" value={roomData.maxOccupancy} onChange={e => handleNumberChange('maxOccupancy', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2011" />
                <p className="text-xs text-gray-500 mt-1" data-id="element-2012">
                  Maximum number of guests allowed
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-4" data-id="element-2013">
              <div className="flex items-center" data-id="element-2014">
                <input type="checkbox" id="allowAdditionalAdults" name="allowAdditionalAdults" checked={roomData.allowAdditionalAdults} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2015" />
                <label htmlFor="allowAdditionalAdults" className="ml-2 text-sm text-gray-700" data-id="element-2016">
                  Allow additional adults
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-2017">
                Allow additional adults (not exceeding total occupancy limit)
              </p>
              <div className="flex items-center mt-2" data-id="element-2018">
                <input type="checkbox" id="allowAdditionalChildren" name="allowAdditionalChildren" checked={roomData.allowAdditionalChildren} onChange={handleCheckboxChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded" data-id="element-2019" />
                <label htmlFor="allowAdditionalChildren" className="ml-2 text-sm text-gray-700" data-id="element-2020">
                  Allow additional children
                </label>
              </div>
              <p className="text-xs text-gray-500 ml-6" data-id="element-2021">
                Allow additional children (not exceeding total occupancy limit)
              </p>
            </div>
            {/* Occupancy Summary */}
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4" data-id="element-2022">
              <div className="flex" data-id="element-2023">
                <AlertCircleIcon className="h-5 w-5 text-red-500 mr-2" data-id="element-2024" />
                <div data-id="element-2025">
                  <p className="text-sm text-red-700" data-id="element-2026">
                    This room has a base occupancy of{' '}
                    {roomData.baseAdultsOccupancy} Adults and{' '}
                    {roomData.baseChildrenOccupancy} Children with a total
                    maximum occupancy of {roomData.maxOccupancy}.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-2027">
                    Additional adults are not allowed.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-2028">
                    Additional children are not allowed.
                  </p>
                  <p className="text-sm text-red-700" data-id="element-2029">
                    Additional cost per child or adults on top of the base
                    occupancy can be configured in rates.
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Bed Configuration */}
          <div className="mb-6" data-id="element-2030">
            <h3 className="text-md font-medium text-gray-800 mb-4" data-id="element-2031">
              Bed configuration<span className="text-red-500" data-id="element-2032">*</span>
            </h3>
            {roomData.bedConfigurations.map((config: any, index: number) => <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4 items-end" data-id="element-2033">
                <div data-id="element-2034">
                  <label htmlFor={`bedType-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2035">
                    Bed type
                  </label>
                  <div className="relative" data-id="element-2036">
                    <select id={`bedType-${index}`} value={config.type} onChange={e => handleBedConfigChange(index, 'type', e.target.value)} className="w-full p-2 border border-gray-300 rounded-md appearance-none pr-8" data-id="element-2037">
                      <option value="" data-id="element-2038">Select bed type</option>
                      {bedTypes.map(type => <option key={type} value={type} data-id="element-2039">
                          {type}
                        </option>)}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none" data-id="element-2040">
                      <svg className="w-4 h-4 fill-current text-gray-500" viewBox="0 0 20 20" data-id="element-2041">
                        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round" data-id="element-2042" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="flex items-center" data-id="element-2043">
                  <div className="flex-grow" data-id="element-2044">
                    <label htmlFor={`bedCount-${index}`} className="block text-sm font-medium text-gray-700 mb-1" data-id="element-2045">
                      Bed count
                    </label>
                    <input type="number" id={`bedCount-${index}`} value={config.count} onChange={e => handleBedConfigChange(index, 'count', e.target.value)} min="1" className="w-full p-2 border border-gray-300 rounded-md" data-id="element-2046" />
                  </div>
                  {roomData.bedConfigurations.length > 1 && <button type="button" onClick={() => removeBedConfig(index)} className="ml-2 mt-6 p-2 text-red-600 hover:text-red-800" data-id="element-2047">
                      <TrashIcon size={18} data-id="element-2048" />
                    </button>}
                </div>
              </div>)}
          </div>
          {/* Submit Button */}
          <div className="flex justify-end" data-id="element-2049">
            <button type="submit" className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700" data-id="element-2050">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>;
}